import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { supabase } from '../../utils/supabaseClient';
import {
  hardResetAuth,
  safeGetUser,
  safeResendSignupEmail,
  safeSignInWithPassword,
  safeSignUpWithPassword,
  safeSignOutLocal,
} from '../../utils/supabaseAuthSafe';
import { isDefinitelyInvalidAuthSessionError } from '../../utils/supabaseAuthErrorClassifier';
import { claimOAuthReferralBonus } from '../../utils/oauthReferralApi';
import { useProfile } from '../../contexts/ProfileContext';
import { migrateGuestDailyAttemptToUser } from '../../utils/games/dailyGuestAttemptMigration';
import AuthCallbackLoading from './AuthCallbackLoading';
import AuthLegalNotice from './AuthLegalNotice';

const VERIFY_PENDING_STORAGE_KEY = 'zi-auth:verify-pending:v1';
const VERIFICATION_TOAST_STORAGE_KEY = 'zi-auth:show-verify-toast:v1';
const WELCOME_TOAST_STORAGE_KEY = 'zi-auth:show-welcome-toast:v1';
const REFERRAL_REFRESH_HINT_STORAGE_KEY = 'zi-auth:show-referral-refresh-hint:v1';
const OAUTH_PENDING_STORAGE_KEY = 'zi-auth:oauth-pending:v1';
const RESEND_COOLDOWN_MS = 60_000;
const RESEND_WINDOW_MS = 15 * 60_000;
const RESEND_WINDOW_LIMIT = 3;
const RESEND_DAY_MS = 24 * 60 * 60_000;
const RESEND_DAY_LIMIT = 8;
const OAUTH_PENDING_TTL_MS = 30 * 60_000;
const STANDARD_STARTING_COINS = 250;
const REFERRAL_BONUS_COINS = 150;
const REFERRAL_STARTING_COINS = STANDARD_STARTING_COINS + REFERRAL_BONUS_COINS;

const normalizeEmail = (value) => String(value || '').trim().toLowerCase();

const normalizeReferralCode = (value) => {
  const trimmed = String(value || '').trim();
  if (!/^[A-Za-z0-9]{4,32}$/.test(trimmed)) {
    return '';
  }
  return trimmed.toUpperCase();
};

const sanitizeTimestamps = (value) => {
  if (!Array.isArray(value)) return [];
  return value
    .map((ts) => Number(ts))
    .filter((ts) => Number.isFinite(ts) && ts > 0)
    .map((ts) => Math.floor(ts))
    .sort((a, b) => a - b);
};

const buildPendingVerification = (
  email,
  nextResendAtMs = 0,
  resendTimestamps = []
) => ({
  email: normalizeEmail(email),
  nextResendAtMs: Number.isFinite(nextResendAtMs) ? Math.floor(nextResendAtMs) : 0,
  resendTimestamps: sanitizeTimestamps(resendTimestamps),
});

const readPendingVerification = () => {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.localStorage.getItem(VERIFY_PENDING_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    const email = normalizeEmail(parsed?.email);
    if (!email) return null;
    return buildPendingVerification(
      email,
      parsed?.nextResendAtMs,
      parsed?.resendTimestamps
    );
  } catch {
    return null;
  }
};

const writePendingVerification = (state) => {
  if (typeof window === 'undefined') return;
  try {
    if (!state?.email) {
      window.localStorage.removeItem(VERIFY_PENDING_STORAGE_KEY);
      return;
    }
    window.localStorage.setItem(
      VERIFY_PENDING_STORAGE_KEY,
      JSON.stringify(buildPendingVerification(state.email, state.nextResendAtMs, state.resendTimestamps))
    );
  } catch {
    // ignore local storage failures
  }
};

const clearPendingVerification = () => {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.removeItem(VERIFY_PENDING_STORAGE_KEY);
  } catch {
    // ignore local storage failures
  }
};

const readPendingOAuth = () => {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.sessionStorage.getItem(OAUTH_PENDING_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    const createdAtMs = Number(parsed?.createdAtMs);
    if (!Number.isFinite(createdAtMs) || Date.now() - createdAtMs > OAUTH_PENDING_TTL_MS) {
      window.sessionStorage.removeItem(OAUTH_PENDING_STORAGE_KEY);
      return null;
    }
    const intent = parsed?.intent === 'signup' ? 'signup' : 'signin';
    const referralCode = normalizeReferralCode(parsed?.referralCode);
    return { intent, referralCode, createdAtMs };
  } catch {
    return null;
  }
};

const writePendingOAuth = ({ intent, referralCode }) => {
  if (typeof window === 'undefined') return;
  try {
    window.sessionStorage.setItem(
      OAUTH_PENDING_STORAGE_KEY,
      JSON.stringify({
        intent: intent === 'signup' ? 'signup' : 'signin',
        referralCode: normalizeReferralCode(referralCode),
        createdAtMs: Date.now(),
      })
    );
  } catch {
    // ignore storage failures
  }
};

const clearPendingOAuth = () => {
  if (typeof window === 'undefined') return;
  try {
    window.sessionStorage.removeItem(OAUTH_PENDING_STORAGE_KEY);
  } catch {
    // ignore storage failures
  }
};

const maskEmail = (value) => {
  const email = normalizeEmail(value);
  if (!email.includes('@')) return email;
  const [localPart, domain] = email.split('@');
  if (!localPart || !domain) return email;
  if (localPart.length <= 2) return `${localPart[0] || '*'}*@${domain}`;
  return `${localPart.slice(0, 2)}***@${domain}`;
};

const formatDurationLabel = (seconds) => {
  if (!Number.isFinite(seconds) || seconds <= 0) return '0s';
  if (seconds < 60) return `${seconds}s`;
  const minutes = Math.ceil(seconds / 60);
  if (minutes < 60) return `${minutes}m`;
  const hours = Math.ceil(minutes / 60);
  return `${hours}h`;
};

const getAuthCallbackParams = () => {
  if (typeof window === 'undefined') return { query: null, hash: null };
  try {
    const query = new URLSearchParams(window.location.search);
    const hashRaw = window.location.hash || '';
    const hash = hashRaw.startsWith('#')
      ? new URLSearchParams(hashRaw.slice(1))
      : null;
    return { query, hash };
  } catch {
    return { query: null, hash: null };
  }
};

const getAuthIntentFromCallback = (pendingOAuth = null) => {
  const { query, hash } = getAuthCallbackParams();
  const rawIntent = String(
    query?.get('intent') || hash?.get('intent') || pendingOAuth?.intent || ''
  ).toLowerCase();
  if (rawIntent === 'signup' || rawIntent === 'signin') return rawIntent;
  return null;
};

const queryOrHashValue = (key) => {
  const { query, hash } = getAuthCallbackParams();
  return query?.get(key) || hash?.get(key) || '';
};

const getReferralCodeFromCallback = (pendingOAuth = null) =>
  normalizeReferralCode(queryOrHashValue('ref')) || normalizeReferralCode(pendingOAuth?.referralCode);

const hasSignupVerificationCallback = () => {
  const { query, hash } = getAuthCallbackParams();
  const typeParam =
    query?.get('type')?.toLowerCase() || hash?.get('type')?.toLowerCase() || '';
  return typeParam === 'signup';
};

const hasAnyAuthCallbackParams = () => {
  const { query, hash } = getAuthCallbackParams();
  const callbackKeys = [
    'type',
    'code',
    'token_hash',
    'access_token',
    'refresh_token',
    'error',
    'error_description',
    'oauth_callback',
  ];
  return callbackKeys.some((key) => Boolean(query?.get(key) || hash?.get(key)));
};

const markVerificationToast = () => {
  if (typeof window === 'undefined') return;
  try {
    window.sessionStorage.setItem(VERIFICATION_TOAST_STORAGE_KEY, '1');
  } catch {
    // ignore storage failures
  }
};

const markWelcomeToast = () => {
  if (typeof window === 'undefined') return;
  try {
    window.sessionStorage.setItem(WELCOME_TOAST_STORAGE_KEY, '1');
  } catch {
    // ignore storage failures
  }
};

const markReferralRefreshHint = () => {
  if (typeof window === 'undefined') return;
  try {
    window.sessionStorage.setItem(REFERRAL_REFRESH_HINT_STORAGE_KEY, '1');
  } catch {
    // ignore storage failures
  }
};

const isLikelyFirstSignIn = (user) => {
  const createdAtMs = Date.parse(String(user?.created_at || ''));
  const lastSignInAtMs = Date.parse(String(user?.last_sign_in_at || ''));
  if (!Number.isFinite(createdAtMs) || !Number.isFinite(lastSignInAtMs)) {
    return false;
  }
  return Math.abs(lastSignInAtMs - createdAtMs) <= 5 * 60_000;
};

const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

const getResendLimitInfo = (pendingState, nowMs = Date.now()) => {
  const now = Number.isFinite(nowMs) ? nowMs : Date.now();
  const dayTimestamps = sanitizeTimestamps(pendingState?.resendTimestamps).filter(
    (ts) => now - ts < RESEND_DAY_MS
  );
  const windowTimestamps = dayTimestamps.filter((ts) => now - ts < RESEND_WINDOW_MS);

  let retryAtMs = Number.isFinite(pendingState?.nextResendAtMs)
    ? Math.floor(pendingState.nextResendAtMs)
    : 0;
  let reason = retryAtMs > now ? 'cooldown' : null;

  if (windowTimestamps.length >= RESEND_WINDOW_LIMIT) {
    const windowRetryAtMs = windowTimestamps[0] + RESEND_WINDOW_MS;
    if (windowRetryAtMs > retryAtMs) {
      retryAtMs = windowRetryAtMs;
      reason = 'window';
    }
  }

  if (dayTimestamps.length >= RESEND_DAY_LIMIT) {
    const dayRetryAtMs = dayTimestamps[0] + RESEND_DAY_MS;
    if (dayRetryAtMs > retryAtMs) {
      retryAtMs = dayRetryAtMs;
      reason = 'day';
    }
  }

  const secondsRemaining = Math.max(0, Math.ceil((retryAtMs - now) / 1000));

  return {
    canResend: secondsRemaining === 0,
    reason,
    retryAtMs,
    secondsRemaining,
    dayTimestamps,
  };
};

const AuthPage = () => {
  const navigate = useNavigate();
  const { refreshCoins } = useProfile();
  const [searchParams] = useSearchParams();
  // Capture referral code from URL (?ref=CODE) -- locked at mount time.
  // Per referral-program-balanced.md: "Referral must be attached at signup
  // time (no retroactive code entry)."
  const [referralCode] = useState(() => {
    return normalizeReferralCode(searchParams.get('ref'));
  });
  const [mode, setMode] = useState(() => {
    const hasPendingVerification = Boolean(readPendingVerification()?.email);
    if (hasPendingVerification || referralCode) return 'signup';
    return 'signin';
  });
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isResending, setIsResending] = useState(false);
  const [pendingVerification, setPendingVerification] = useState(() =>
    readPendingVerification()
  );
  const [clockNowMs, setClockNowMs] = useState(() => Date.now());
  const [isAuthCallbackProcessing, setIsAuthCallbackProcessing] = useState(
    () => hasAnyAuthCallbackParams() || Boolean(readPendingOAuth())
  );

  const navigateHome = useCallback(() => {
    navigate('/', { replace: true });
  }, [navigate]);

  const persistPendingVerification = useCallback((nextState) => {
    setPendingVerification(nextState);
    if (nextState) {
      writePendingVerification(nextState);
    } else {
      clearPendingVerification();
    }
  }, []);

  const clearPendingVerificationState = useCallback(() => {
    persistPendingVerification(null);
  }, [persistPendingVerification]);

  const resendLimitInfo = useMemo(() => {
    if (!pendingVerification?.email) return null;
    return getResendLimitInfo(pendingVerification, clockNowMs);
  }, [pendingVerification, clockNowMs]);

  const resendSecondsRemaining = resendLimitInfo?.secondsRemaining ?? 0;
  const showPendingVerification = mode === 'signup' && Boolean(pendingVerification?.email);
  const pendingVerificationEmailLabel = useMemo(
    () => maskEmail(pendingVerification?.email || ''),
    [pendingVerification?.email]
  );
  const resendRateLimited =
    resendLimitInfo?.reason === 'window' || resendLimitInfo?.reason === 'day';

  useEffect(() => {
    if (!showPendingVerification || resendSecondsRemaining <= 0) return undefined;
    const id = window.setInterval(() => {
      setClockNowMs(Date.now());
    }, 1000);
    return () => window.clearInterval(id);
  }, [showPendingVerification, resendSecondsRemaining]);

  useEffect(() => {
    let isMounted = true;

    const checkSession = async () => {
      const pendingOAuth = readPendingOAuth();
      const hasOAuthPending = Boolean(pendingOAuth);
      const hasSignupVerification = hasSignupVerificationCallback();
      const hasUrlAuthCallback = hasAnyAuthCallbackParams();
      const hasAuthCallback = hasUrlAuthCallback || hasOAuthPending;
      const callbackIntent = getAuthIntentFromCallback(pendingOAuth);
      const callbackReferralCode = getReferralCodeFromCallback(pendingOAuth);
      const maxAttempts = hasUrlAuthCallback ? 8 : hasOAuthPending ? 3 : 1;

      if (isMounted) {
        setIsAuthCallbackProcessing(hasAuthCallback);
      }

      for (let attempt = 1; attempt <= maxAttempts; attempt += 1) {
        if (!isMounted) return;

        let data = null;
        let error = null;
        try {
          const result = await safeGetUser(supabase, { timeoutMs: 7000 });
          data = result?.data || null;
          error = result?.error || null;
        } catch {
          data = null;
          error = null;
        }

        if (error) {
          // Only clear local session if it's *definitely* invalid. Otherwise,
          // transient network issues would sign users out when they visit /auth.
          if (isDefinitelyInvalidAuthSessionError(error)) {
            // Don't hang here; local sign-out is enough to reset UI state.
            await safeSignOutLocal(supabase).catch(() => {});
            if (hasAuthCallback) {
              clearPendingOAuth();
            }
            if (isMounted) {
              setIsAuthCallbackProcessing(false);
            }
            return;
          }
          if (attempt < maxAttempts) {
            await wait(400 * attempt);
            continue;
          }
          if (hasAuthCallback) {
            clearPendingOAuth();
          }
          if (isMounted) {
            setIsAuthCallbackProcessing(false);
          }
          return;
        }

        if (data?.user) {
          let shouldRefreshCoinsAfterCallback = false;
          const isFirstSignIn = isLikelyFirstSignIn(data.user);
          const hasReferralSignupMetadata = Boolean(
            normalizeReferralCode(
              data.user?.user_metadata?.referral_code ||
                data.user?.raw_user_meta_data?.referral_code
            )
          );

          if (hasAuthCallback && callbackIntent === 'signup' && callbackReferralCode) {
            try {
              const claimResult = await claimOAuthReferralBonus({
                referralCode: callbackReferralCode,
              });
              shouldRefreshCoinsAfterCallback = true;
              if (!claimResult?.processed) {
                try {
                  window.sessionStorage.setItem(
                    'zi-auth:last-referral-claim-reason',
                    String(claimResult?.reason || 'not_processed')
                  );
                } catch {
                  // ignore storage failures
                }
              }
            } catch {
              // Non-blocking: user still signs in even if referral claim fails.
            }
          }

          if (hasAuthCallback && callbackIntent === 'signup') {
            shouldRefreshCoinsAfterCallback = true;
          }
          if (hasSignupVerification && hasReferralSignupMetadata) {
            markReferralRefreshHint();
          }
          if (hasSignupVerification || isFirstSignIn) {
            shouldRefreshCoinsAfterCallback = true;
          }

          const shouldShowVerificationToast =
            hasSignupVerification ||
            (hasAuthCallback && Boolean(pendingVerification?.email));
          const shouldShowWelcomeToast =
            shouldShowVerificationToast ||
            isFirstSignIn ||
            (hasAuthCallback && callbackIntent === 'signup');

          if (shouldShowVerificationToast) {
            markVerificationToast();
          }
          if (shouldShowWelcomeToast) {
            markWelcomeToast();
          }

          if (shouldRefreshCoinsAfterCallback) {
            try {
              await refreshCoins?.();
            } catch {
              // Non-blocking: auth flow should not fail on coin refresh.
            }
          }

          const shouldAttemptGuestDailyMigration =
            isFirstSignIn ||
            hasSignupVerification ||
            (hasAuthCallback && callbackIntent === 'signup');
          if (shouldAttemptGuestDailyMigration) {
            try {
              await migrateGuestDailyAttemptToUser({ userId: data.user.id });
            } catch (migrationError) {
              console.error('[auth] Failed to migrate guest daily attempt:', migrationError);
            }
          }

          clearPendingOAuth();
          clearPendingVerificationState();
          navigateHome();
          return;
        }

        if (attempt < maxAttempts) {
          await wait(400 * attempt);
        }
      }

      if (hasAuthCallback) {
        clearPendingOAuth();
      }
      if (isMounted) {
        setIsAuthCallbackProcessing(false);
      }
    };

    checkSession();

    const { data: authListener } = supabase.auth.onAuthStateChange((event, session) => {
      if (!isMounted) return;
      if (!session?.user) return;
      if (
        event === 'SIGNED_IN' ||
        event === 'INITIAL_SESSION' ||
        event === 'TOKEN_REFRESHED' ||
        event === 'USER_UPDATED'
      ) {
        checkSession();
      }
    });

    return () => {
      isMounted = false;
      authListener?.subscription?.unsubscribe();
    };
  }, [clearPendingVerificationState, navigateHome, pendingVerification?.email, refreshCoins]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setMessage('');
    setIsSubmitting(true);

    try {
      if (mode === 'signup') {
        const normalizedEmail = normalizeEmail(email);
        const signUpOpts = {
          email: normalizedEmail,
          password,
          emailRedirectTo: `${window.location.origin}/auth`,
          timeoutMs: 10000,
        };
        // Attach referral code to user_metadata when present
        if (referralCode) {
          signUpOpts.data = { referral_code: referralCode };
        }
        const { data, error: signUpError } = await safeSignUpWithPassword(
          supabase,
          signUpOpts
        );
        if (signUpError) throw signUpError;

        if (data?.session?.user) {
          try {
            await migrateGuestDailyAttemptToUser({ userId: data.session.user.id });
          } catch (migrationError) {
            console.error('[auth] Failed to migrate guest daily attempt:', migrationError);
          }
          markWelcomeToast();
          clearPendingOAuth();
          clearPendingVerificationState();
          navigateHome();
        } else {
          markWelcomeToast();
          clearPendingOAuth();
          const now = Date.now();
          const nextPendingState = buildPendingVerification(
            normalizedEmail,
            now + RESEND_COOLDOWN_MS,
            []
          );
          persistPendingVerification(nextPendingState);
          setClockNowMs(now);
          setMessage(
            'Email sent! Check your inbox, spam, and promotions to verify your account.'
          );
          setPassword('');
          setShowPassword(false);
        }
      } else {
        const { error: signInError } = await safeSignInWithPassword(supabase, {
          email: normalizeEmail(email),
          password,
          timeoutMs: 10000,
        });
        if (signInError) throw signInError;
        clearPendingOAuth();
        clearPendingVerificationState();
        navigateHome();
      }
    } catch (err) {
      if (err?.code === 'TIMEOUT') {
        setError(
          'Sign-in timed out. This usually means the auth request is being blocked (VPN/adblock/firewall) or Supabase is unreachable. Try disabling blockers and retry.'
        );
      } else if (mode === 'signup') {
        setError('Could not create account. Please check your details and try again.');
      } else {
        setError('Invalid login credentials or account not ready. Please try again.');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleResendVerification = async () => {
    setError('');
    setMessage('');

    const pendingEmail = normalizeEmail(pendingVerification?.email || '');
    if (!pendingEmail) {
      setError('Use the sign up form first to send a verification email.');
      return;
    }

    const now = Date.now();
    const limits = getResendLimitInfo(pendingVerification, now);
    setClockNowMs(now);
    if (!limits.canResend) {
      if (limits.reason === 'window' || limits.reason === 'day') {
        setError('Too many resend attempts. Please try again later.');
      } else {
        setError(`Please wait ${limits.secondsRemaining}s before trying again.`);
      }
      return;
    }

    setIsResending(true);
    try {
      const { error: resendError } = await safeResendSignupEmail(supabase, {
        email: pendingEmail,
        emailRedirectTo: `${window.location.origin}/auth`,
        timeoutMs: 10000,
      });
      if (resendError) throw resendError;

      const sentAt = Date.now();
      const nextPendingState = buildPendingVerification(
        pendingEmail,
        sentAt + RESEND_COOLDOWN_MS,
        [...limits.dayTimestamps, sentAt]
      );
      persistPendingVerification(nextPendingState);
      setClockNowMs(sentAt);
      setMessage('Verification email sent. Please check your inbox and spam folder.');
    } catch (err) {
      const errMessage = String(err?.message || '').toLowerCase();
      if (err?.code === 'TIMEOUT') {
        setError(
          'Request timed out. Check your connection/VPN/adblock and try again.'
        );
      } else if (
        err?.status === 429 ||
        errMessage.includes('rate limit') ||
        errMessage.includes('too many')
      ) {
        setError('Too many resend attempts. Please try again later.');
      } else {
        setError('Failed to resend verification email. Please try again.');
      }
    } finally {
      setIsResending(false);
    }
  };

  const handleUseDifferentEmail = () => {
    setError('');
    setMessage('');
    setEmail('');
    setPassword('');
    setShowPassword(false);
    clearPendingVerificationState();
  };

  const handleGoogleSignIn = async () => {
    setError('');
    setMessage('');
    setIsAuthCallbackProcessing(true);
    writePendingOAuth({ intent: mode, referralCode });
    const callbackQuery = new URLSearchParams({
      oauth_callback: '1',
      intent: mode === 'signup' ? 'signup' : 'signin',
    });
    if (referralCode) {
      callbackQuery.set('ref', referralCode);
    }
    const oauthOptions = {
      redirectTo: `${window.location.origin}/auth?${callbackQuery.toString()}`,
    };
    const { error: oauthError } = await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: oauthOptions,
    });
    if (oauthError) {
      clearPendingOAuth();
      setIsAuthCallbackProcessing(false);
      setError(oauthError.message || 'Google sign-in failed');
    }
  };

  const handleResetAuth = async () => {
    setError('');
    setMessage('');
    setIsSubmitting(true);
    try {
      await hardResetAuth(supabase);
      setMessage('Local auth session cleared (hard reset). Try logging in again.');
    } catch (err) {
      setError(err?.message || 'Failed to reset auth session.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const showAuthCallbackLoading = isAuthCallbackProcessing && !showPendingVerification;

  return (
    <div className="auth-page">
      <div className="auth-card">
        <h2>
          {showAuthCallbackLoading
            ? 'Signing you in'
            : showPendingVerification
            ? 'Verify your email'
            : mode === 'signup'
              ? 'Create your account'
              : 'Welcome back'}
        </h2>
        {!showPendingVerification && !showAuthCallbackLoading && (
          <p className="auth-subtitle">
            {mode === 'signup'
              ? 'Create an account to save progress, streaks, and coins.'
              : 'Log in to access your coins, streaks, and power-ups.'}
          </p>
        )}
        {referralCode && !showPendingVerification && !showAuthCallbackLoading && (
          <p className={`auth-referral-badge${mode === 'signin' ? ' auth-referral-badge--signin' : ''}`}>
            {mode === 'signup'
              ? `Referral detected. Sign up now to start with ${REFERRAL_STARTING_COINS} coins (${STANDARD_STARTING_COINS} standard + ${REFERRAL_BONUS_COINS} bonus).`
              : `Referral detected. This bonus applies to new sign-ups only. Switch to Sign up to start with ${REFERRAL_STARTING_COINS} coins instead of ${STANDARD_STARTING_COINS}.`}
          </p>
        )}

        {showAuthCallbackLoading ? (
          <AuthCallbackLoading />
        ) : showPendingVerification ? (
          <div className="auth-verify-panel">
            <p className="auth-verify-title">Email sent!</p>
            <p className="auth-subtitle">
              {pendingVerificationEmailLabel
                ? `We sent a verification link to ${pendingVerificationEmailLabel}.`
                : 'We sent a verification link to your email address.'}
            </p>
            <p className="auth-subtitle">
              Do not forget to check your Spam and Promotions folders.
            </p>

            <button
              className="auth-button auth-button--secondary"
              type="button"
              onClick={handleResendVerification}
              disabled={isResending || !resendLimitInfo?.canResend}
            >
              {isResending ? 'Sending...' : "Didn't see it? Resend link"}
            </button>
            <p className="auth-resend-hint">
              {resendRateLimited
                ? `Too many resend attempts. Try again in ${formatDurationLabel(
                    resendSecondsRemaining
                  )}.`
                : resendSecondsRemaining > 0
                  ? `You can resend in ${resendSecondsRemaining}s.`
                  : 'Use resend if the email has not arrived yet.'}
            </p>

            <button
              type="button"
              className="auth-toggle"
              onClick={handleUseDifferentEmail}
              disabled={isResending}
            >
              Use a different email
            </button>
            <button
              type="button"
              className="auth-toggle"
              onClick={() => setMode('signin')}
              disabled={isResending}
            >
              Already verified? Log in
            </button>
          </div>
        ) : (
          <>
            <button
              className="auth-button auth-google"
              type="button"
              onClick={handleGoogleSignIn}
            >
              <span className="auth-google-icon" aria-hidden="true">
                <svg viewBox="0 0 18 18" xmlns="http://www.w3.org/2000/svg">
                  <path
                    fill="#4285F4"
                    d="M17.64 9.2045c0-.6382-.0573-1.2518-.1636-1.8409H9v3.4818h4.8436c-.2086 1.125-.8427 2.0782-1.7959 2.7164v2.2582h2.9086c1.7018-1.5668 2.6837-3.8741 2.6837-6.6155z"
                  />
                  <path
                    fill="#34A853"
                    d="M9 18c2.43 0 4.4673-.8068 5.9564-2.1791l-2.9086-2.2582c-.8068.54-1.8377.8591-3.0477.8591-2.3441 0-4.3282-1.5827-5.0364-3.7091H.9573v2.3318C2.4382 15.9832 5.4818 18 9 18z"
                  />
                  <path
                    fill="#FBBC05"
                    d="M3.9636 10.7127c-.18-.54-.2836-1.1168-.2836-1.7127s.1036-1.1727.2836-1.7127V4.9555H.9573A8.996 8.996 0 0 0 0 9c0 1.4523.3477 2.8277.9573 4.0445l3.0063-2.3318z"
                  />
                  <path
                    fill="#EA4335"
                    d="M9 3.5782c1.3214 0 2.5077.4541 3.44 1.3459l2.5814-2.5814C13.4632.8918 11.4268 0 9 0 5.4818 0 2.4382 2.0168.9573 4.9555l3.0063 2.3318C4.6718 5.1609 6.6559 3.5782 9 3.5782z"
                  />
                </svg>
              </span>
              <span className="auth-google-label">Continue with Google</span>
            </button>

            <div className="auth-divider">or</div>

            <form onSubmit={handleSubmit} className="auth-form">
              <label className="auth-field">
                Email
                <input
                  className="auth-input"
                  type="email"
                  autoComplete="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                />
              </label>
              <label className="auth-field">
                Password
                <div className="auth-password-wrap">
                  <input
                    className="auth-input auth-input--password"
                    type={showPassword ? 'text' : 'password'}
                    autoComplete={mode === 'signup' ? 'new-password' : 'current-password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                  />
                  <button
                    type="button"
                    className="auth-password-toggle"
                    onClick={() => setShowPassword((value) => !value)}
                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                    aria-pressed={showPassword}
                    disabled={isSubmitting}
                  >
                    <svg
                      className="auth-password-icon"
                      viewBox="0 0 24 24"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                      aria-hidden="true"
                    >
                      <path
                        d="M1.75 12s3.6-6.5 10.25-6.5S22.25 12 22.25 12s-3.6 6.5-10.25 6.5S1.75 12 1.75 12Z"
                        stroke="currentColor"
                        strokeWidth="1.8"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                      <circle cx="12" cy="12" r="3.1" stroke="currentColor" strokeWidth="1.8" />
                      {!showPassword && (
                        <path
                          d="M4 4l16 16"
                          stroke="currentColor"
                          strokeWidth="1.8"
                          strokeLinecap="round"
                        />
                      )}
                    </svg>
                  </button>
                </div>
              </label>
              <button className="auth-button" type="submit" disabled={isSubmitting}>
                {isSubmitting
                  ? 'Please wait...'
                  : mode === 'signup'
                    ? 'Sign up'
                    : 'Log in'}
              </button>
            </form>
          </>
        )}

        {error && <div className="auth-error">{error}</div>}
        {message && <div className="auth-message">{message}</div>}
        {!showPendingVerification && !showAuthCallbackLoading && <AuthLegalNotice />}

        {!showPendingVerification && !showAuthCallbackLoading && (
          <>
            <button
              type="button"
              className="auth-toggle"
              onClick={() => navigate('/auth/forgot')}
              disabled={isSubmitting}
            >
              Forgot password?
            </button>

            <button
              type="button"
              className="auth-toggle"
              onClick={handleResetAuth}
              disabled={isSubmitting}
            >
              Having trouble? Reset local session
            </button>

            <button
              type="button"
              className="auth-toggle"
              onClick={() => setMode(mode === 'signup' ? 'signin' : 'signup')}
            >
              {mode === 'signup'
                ? 'Already have an account? Log in'
                : 'No account yet? Sign up'}
            </button>
          </>
        )}
      </div>
    </div>
  );
};

export default AuthPage;
